import React, { Children } from 'react';
import { HomeSvg } from '@components/svg/home';
import { ActivitiesSvg } from '@components/svg/activities';
import { WeekScheduleSvg } from '@components/svg/week-schedule';
import { SettingsSvg } from '@components/svg/settings';
import { ErrorSvg } from '@projtech-package/react-components/lib';

const AuthGuard = React.lazy(() => import('@guard/auth-guard'));
const HomePage = React.lazy(() => import('@pages/home'));
const DetailPage = React.lazy(() => import('@pages/detail'));
const QuranPage = React.lazy(() => import('@pages/quran'));
const QuranIframePage = React.lazy(() => import('@pages/quran/iframe'));
const QiblaCompassPage = React.lazy(() => import('@pages/qibla'));
const PrayerTimesPage = React.lazy(() => import('@pages/prayer-times'));
const ContactUsPage = React.lazy(() => import('@pages/contact-us'));
const SettingsPage = React.lazy(() => import('@pages/settings'));
const SuccessPage = React.lazy(() => import('@pages/success/'));
const SubscribeSuccessPage = React.lazy(() => import('@pages/learn-quran/subscribe/success'));
const UserLoginPage = React.lazy(() => import('@pages/auth/login'));
const AutoLoginPage = React.lazy(() => import('@pages/auth/autologin'));
const ChooseAccountPage = React.lazy(() => import('@pages/auth/choose-account'));
const UserRegisterPage = React.lazy(() => import('@pages/auth/register'));
const ForgetPasswordPage = React.lazy(() => import('@pages/auth/forget-password'));
const UserLogoutPage = React.lazy(() => import('@pages/auth/logout'));
const AdminHomePage = React.lazy(() => import('@pages/admin/home'));
const AdminSeminarPage = React.lazy(() => import('@pages/admin/seminar'));
const AdminStudentsPage = React.lazy(() => import('@pages/admin/students'));
const TeacherHomePage = React.lazy(() => import('@pages/teacher/home'));
const HomeWorkStudentsPage = React.lazy(() => import('@pages/teacher/session/students'));
const TeacherDynamicIframePage = React.lazy(() => import('@pages/teacher/dynamic-iframe'));
const TeacherIframePage = React.lazy(() => import('@pages/teacher/iframe'));
const TeacherSessionPage = React.lazy(() => import('@pages/teacher/session'));
const TeacherStudentsWeeklySessionPage = React.lazy(() => import('@pages/teacher/student/weekly-session'));
const TeacherAccountPage = React.lazy(() => import('@pages/teacher/session/account'));
const LearnQuranPage = React.lazy(() => import('@pages/learn-quran'));
const SingleLearnQuranPage = React.lazy(() => import('@pages/learn-quran/single'));
const SubscribeLearnQuranPage = React.lazy(() => import('@pages/learn-quran/subscribe'));
const StudentHomePage = React.lazy(() => import('@pages/student/home'));
const SubscriberPage = React.lazy(() => import('@pages/student/subscriber'));
const SingleSubscriberPage = React.lazy(() => import('@pages/student/subscriber/single'));
const DetailSubscriberPage = React.lazy(() => import('@pages/student/subscriber/detail-lagacy'));
const TeacherStudentsPage = React.lazy(() => import('@pages/teacher/student'));
const TeacherStudentSinglePage = React.lazy(() => import('@pages/teacher/student/single'));
const TeacherStudentAttendancePage = React.lazy(() => import('@pages/teacher/student/attendance'));
const TeacherStudentsHomeworkPage = React.lazy(() => import('@pages/teacher/student/homework'));
const TeacherStudentsPlanPage = React.lazy(() => import('@pages/teacher/student/plan'));
const DynamicIframePage = React.lazy(() => import('@pages/teacher/dynamic-iframe'));
const SupportPage = React.lazy(() => import('@pages/support'));
const InvoicePage = React.lazy(() => import('@pages/student/invoice'));
const QuranLagacyPage = React.lazy(() => import('@pages/quran/index.lagacy'));

export const RoutesData = {
	home: {
		route: '/',
		title: 'Home',
		element: <HomePage />,
		svg: HomeSvg,
		isBottomNav: false,
	},
	account: {
		route: '/account',
		title: 'Account',
		element: (
			<AuthGuard>
				<TeacherAccountPage />
			</AuthGuard>
		),
		svg: (color?: string) => (
			<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path
					d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z"
					stroke={color ? color : '#3B4057'}
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M20.59 22C20.59 18.13 16.74 15 12 15C7.26003 15 3.41003 18.13 3.41003 22"
					stroke="#3B4057"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
		),
		isBottomNav: true,
	},
	learnQuran: {
		route: '/learn-quran',
		title: 'Quran lernen',
		element: <LearnQuranPage />,
		svg: () => (
			<svg width="48" height="28" viewBox="0 0 48 28" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path
					d="M12.6166 15.1307L18.6987 17.7282L14.7367 21.0205C14.684 21.0643 14.6416 21.1192 14.6124 21.1812C14.5833 21.2432 14.5682 21.3109 14.5682 21.3794V23.317C14.5682 23.3942 14.5874 23.4702 14.624 23.5382C14.6606 23.6062 14.7135 23.664 14.778 23.7066C14.8425 23.7491 14.9165 23.7749 14.9934 23.7818C15.0703 23.7886 15.1478 23.7763 15.2187 23.7459L23.9995 19.9897L32.7803 23.7459C32.8513 23.7763 32.9287 23.7886 33.0057 23.7818C33.0826 23.7749 33.1566 23.7491 33.2211 23.7066C33.2855 23.664 33.3385 23.6062 33.3751 23.5382C33.4117 23.4702 33.4309 23.3942 33.4309 23.317V21.3794C33.4309 21.3109 33.4158 21.2432 33.3867 21.1812C33.3575 21.1192 33.3151 21.0643 33.2624 21.0205L29.3009 17.7282L35.3834 15.1303C35.4862 15.0864 35.5699 15.007 35.6191 14.9066C35.6684 14.8062 35.68 14.6915 35.6517 14.5833L33.2204 5.27332C33.1943 5.17338 33.1358 5.08491 33.0541 5.02175C32.9723 4.95859 32.872 4.9243 32.7687 4.92425H31.2982L31.2044 4.56445C31.189 4.50506 31.162 4.44932 31.1249 4.40045C31.0878 4.35158 31.0414 4.31057 30.9883 4.27978C30.9356 4.24858 30.8771 4.22822 30.8164 4.21989C30.7556 4.21155 30.6938 4.21542 30.6346 4.23125C26.6871 5.26445 24.7621 7.16565 23.9995 8.13492C23.2375 7.16565 21.312 5.26445 17.364 4.23125C17.3047 4.21545 17.2429 4.21154 17.1821 4.21976C17.1213 4.22798 17.0627 4.24817 17.0097 4.27914C16.9567 4.31012 16.9104 4.35128 16.8734 4.40025C16.8364 4.44922 16.8095 4.50502 16.7942 4.56445L16.7004 4.92425H15.2313C15.128 4.92422 15.0276 4.95848 14.9459 5.02165C14.8641 5.08482 14.8056 5.17334 14.7796 5.27332L12.3483 14.5833C12.3199 14.6916 12.3315 14.8064 12.3807 14.9069C12.43 15.0074 12.5137 15.0868 12.6166 15.1307ZM15.5015 21.5987L19.6633 18.1403L22.8095 19.4843L15.5015 22.6105V21.5987ZM32.4975 21.5987V22.61L25.19 19.4843L28.3363 18.1407L32.4975 21.5987ZM30.4209 5.25885L32.5661 13.4778L24.4667 16.9363V9.08505C24.7705 8.61418 26.3595 6.44372 30.4209 5.25885ZM23.5329 9.08505V16.9367L15.4334 13.4783L17.5782 5.25932C21.6424 6.44465 23.2309 8.61745 23.5329 9.08505ZM15.5916 5.85805H16.4568L14.4282 13.6313C14.4 13.7395 14.4116 13.8542 14.4608 13.9546C14.5101 14.055 14.5937 14.1344 14.6965 14.1783L23.8161 18.0721C23.8227 18.0749 23.8297 18.074 23.8357 18.0763C23.8885 18.0964 23.9431 18.1099 23.9991 18.1099C24.0551 18.1099 24.1097 18.0964 24.1624 18.0763C24.1689 18.074 24.1759 18.0749 24.182 18.0721L33.3016 14.1783C33.4044 14.1344 33.4881 14.055 33.5373 13.9546C33.5866 13.8542 33.5982 13.7395 33.5699 13.6313L31.5409 5.85805H32.4075L34.6461 14.4303L28.2056 17.1808L28.1953 17.1855L24 18.9775L19.8023 17.1845L19.7953 17.1817L13.3535 14.4307L15.5916 5.85805Z"
					fill="#3B4057"
				/>
			</svg>
		),
		isBottomNav: true,
		children: {
			iframe: {
				route: '/iframe/learn-quran',
				title: 'Iframe',
				element: <SubscribeLearnQuranPage isIframe />,
				svg: HomeSvg,
				isBottomNav: false,
				children: {
					success: {
						route: '/iframe/learn-quran/success',
						title: 'Success',
						element: <SubscribeSuccessPage />,
					},
				},
			},

			detail: {
				route: '/learn-quran/:id',
				title: 'Detail',
				element: <SingleLearnQuranPage />,
				svg: HomeSvg,
				isBottomNav: false,
			},
			subscribe: {
				route: '/learn-quran/:planId/:programId/subscribe',
				title: 'Subscribe',
				element: <SubscribeLearnQuranPage isIframe={false} />,
				svg: HomeSvg,
				isBottomNav: false,
			},
			user: {
				route: '/learn-quran/:userId/subscribe',
				title: 'Subscribe',
				element: (
					<AuthGuard>
						<SubscribeLearnQuranPage isIframe={false} />
					</AuthGuard>
				),
				svg: HomeSvg,
				isBottomNav: false,
			},
			success: {
				route: '/learn-quran/success',
				title: 'Success',
				element: <SubscribeSuccessPage />,
			},
			successPage: {
				route: '/learn-quran/subscribe/success',
				title: 'Success',
				element: <SubscribeSuccessPage />,
			},
		},
	},
	admin: {
		route: '/admin',
		element: (
			<AuthGuard>
				<AdminHomePage />
			</AuthGuard>
		),
		children: {
			seminar: {
				route: '/admin/seminar',
				title: 'Seminar',
				element: (
					<AuthGuard>
						<AdminSeminarPage />
					</AuthGuard>
				),
				svg: HomeSvg,
				isBottomNav: false,
				children: {
					students: {
						route: '/admin/seminar/:seminarId/students/',
						title: 'students',
						element: (
							<AuthGuard>
								<AdminStudentsPage />
							</AuthGuard>
						),
						svg: HomeSvg,
						isBottomNav: false,
					},
				},
			},
			weeklyHomeWork: {
				route: '/admin/:seminarId/students/:date',
				title: 'students',
				element: (
					<AuthGuard>
						<AdminStudentsPage />
					</AuthGuard>
				),
				svg: HomeSvg,
				isBottomNav: false,
				children: {
					single: {
						route: '/admin/students/:id',
						title: 'Schüler Detail',
						element: (
							<AuthGuard>
								<SingleSubscriberPage />
							</AuthGuard>
						),
						svg: HomeSvg,
						isBottomNav: false,
					},
				},
			},
		},
	},
	teacher: {
		route: '/teacher',
		title: 'Lehrer',
		element: (
			<AuthGuard>
				<TeacherHomePage />
			</AuthGuard>
		),
		svg: HomeSvg,
		isBottomNav: false,
		children: {
			attendance: {
				route: '/teacher/attendances',
				title: 'Schüler Anwesenheit',
				element: (
					<AuthGuard>
						<TeacherStudentAttendancePage />
					</AuthGuard>
				),
				svg: HomeSvg,
				isBottomNav: false,
			},
			homework: {
				route: '/teacher/homework',
				title: 'Hausaufgaben',
				element: (
					<AuthGuard>
						<TeacherStudentsHomeworkPage />
					</AuthGuard>
				),
				svg: HomeSvg,
				isBottomNav: false,
			},
			weeklySession: {
				route: '/teacher/weekly-session',
				title: 'Wöchentliche Session',
				element: (
					<AuthGuard>
						<TeacherStudentsWeeklySessionPage />
					</AuthGuard>
				),
				svg: HomeSvg,
				isBottomNav: false,
			},
			student: {
				route: '/teacher/students',
				title: 'Schüler',
				element: (
					<AuthGuard>
						<TeacherStudentsPage />
					</AuthGuard>
				),
				svg: HomeSvg,
				isBottomNav: false,
				children: {
					single: {
						route: '/teacher/students/:id',
						title: 'Schüler Detail',
						element: (
							<AuthGuard>
								<TeacherStudentSinglePage />
							</AuthGuard>
						),
						svg: HomeSvg,
						isBottomNav: false,
					},
				},
			},
			plan: {
				route: '/teacher/plan',
				title: 'Pläne',
				element: (
					<AuthGuard>
						<TeacherStudentsPlanPage />
					</AuthGuard>
				),
			},
		},
	},

	iframe: {
		route: '/teacher/iframe',
		title: 'Iframe',
		element: (
			<AuthGuard>
				<TeacherDynamicIframePage />
			</AuthGuard>
		),
		svg: HomeSvg,
		isBottomNav: false,
		children: {
			dynamic: {
				route: '/iframe/:id',
				title: 'Home',
				element: <DynamicIframePage />,
				svg: HomeSvg,
				isBottomNav: false,
			},
		},
	},
	student: {
		route: '/student',
		title: 'Schüler',
		element: (
			<AuthGuard>
				<StudentHomePage />
			</AuthGuard>
		),
		svg: HomeSvg,
		isBottomNav: false,
		children: {
			quran: {
				route: '/student/quran',
				title: 'Quran',
				element: (
					<AuthGuard>
						<QuranLagacyPage />
					</AuthGuard>
				),
				svg: HomeSvg,
				isBottomNav: false,
			},
			invoice: {
				route: '/student/invoice',
				title: 'Rechnungen',
				element: (
					<AuthGuard>
						<InvoicePage />
					</AuthGuard>
				),
				svg: HomeSvg,
				isBottomNav: false,
			},
			subscriber: {
				route: '/student/subscriber',
				title: 'Abonnent',
				element: (
					<AuthGuard>
						<SubscriberPage />
					</AuthGuard>
				),
				svg: HomeSvg,
				isBottomNav: false,
				children: {
					single: {
						route: '/student/subscriber/:id',
						title: 'Abonnent Detail',
						element: (
							<AuthGuard>
								<SingleSubscriberPage />
							</AuthGuard>
						),
						svg: HomeSvg,
						isBottomNav: false,
					},
					detail: {
						route: '/student/subscriber/detail/:id',
						title: 'Abonnent Detail',
						element: (
							<AuthGuard>
								<DetailSubscriberPage />
							</AuthGuard>
						),
						svg: HomeSvg,
						isBottomNav: false,
					},
				},
			},
		},
	},
	settings: {
		route: '/settings',
		title: 'Einstellungen',
		element: <SettingsPage />,
		svg: SettingsSvg,
		isBottomNav: true,
	},
	quran: {
		route: '/quran',
		title: 'Quran',
		element: <QuranPage />,
		svg: SettingsSvg,
		isBottomNav: false,
		children: {
			iframe: {
				route: '/quran/iframe',
				title: 'Quran Iframe',
				element: <QuranLagacyPage />,
			},
		},
	},
	qibla: {
		route: '/qibla',
		title: 'Qibla',
		element: <QiblaCompassPage />,
		svg: SettingsSvg,
		isBottomNav: false,
	},
	detail: {
		route: '/detail/:type/:detailId',
		element: <DetailPage />,
		isBottomNav: false,
		title: 'Detail',
		svg: SettingsSvg,
	},
	['prayer-times']: {
		route: '/prayer-times',
		title: 'Gebet',
		element: <PrayerTimesPage />,
		svg: () => (
			<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path
					d="M11.7541 0.822062C10.1298 1.14522 9.37576 2.60367 9.37576 3.75031C9.37576 5.14356 10.3353 6.31571 11.6265 6.64879V7.38581C10.7959 8.67985 8.94489 9.56428 7.19304 10.6358C5.2456 11.8264 3.7673 13.3146 3.74888 15.7496C3.73896 17.3668 4.19393 18.5645 4.70559 19.3922C4.96355 19.8344 5.62545 19.4276 5.3434 18.9968C4.89977 18.2782 4.48165 17.2307 4.50007 15.751C4.52842 13.4521 6.03081 12.2502 7.5899 11.2722C9.15182 10.29 10.9419 9.52743 12.0007 8.14551C13.058 9.52743 14.7447 10.1908 16.4101 11.2722C18.1081 12.3735 19.5382 13.5683 19.4999 15.751C19.4701 17.5369 18.9429 19.1442 17.5156 20.2497H4.87284C4.57508 20.2519 4.29021 20.3715 4.08005 20.5824C3.8699 20.7934 3.75141 21.0787 3.75029 21.3765V22.8732C3.75029 23.0801 3.91612 23.2488 4.12306 23.2502H19.8755C19.9251 23.2504 19.9742 23.2408 20.02 23.2219C20.0658 23.203 20.1075 23.1753 20.1425 23.1402C20.1775 23.1052 20.2053 23.0635 20.2242 23.0177C20.2431 22.9719 20.2527 22.9228 20.2525 22.8732V21.3765C20.2507 21.0782 20.1313 20.7927 19.9204 20.5818C19.7095 20.3709 19.424 20.2515 19.1257 20.2497H18.6396C19.9733 18.7657 20.2667 17.0436 20.2511 15.7496C20.2185 13.1601 18.5985 11.7612 16.8069 10.6358C15.0693 9.54444 13.2069 8.67985 12.3763 7.38865V6.74659C13.0339 6.74728 13.6735 6.53117 14.1959 6.13173C14.7183 5.73228 15.0945 5.17176 15.2663 4.53694C15.4165 4.08906 14.9573 3.8396 14.688 4.07205C14.4139 4.2982 14.081 4.4416 13.7283 4.48546C13.3756 4.52932 13.0178 4.47183 12.6966 4.31972C12.3754 4.1676 12.1042 3.92715 11.9147 3.62651C11.7252 3.32587 11.6252 2.97748 11.6265 2.6221C11.6265 2.18981 11.7753 1.77169 12.0489 1.43719C12.0947 1.38192 12.1236 1.3147 12.1324 1.24347C12.1412 1.17225 12.1293 1.1 12.0983 1.03529C12.0673 0.970576 12.0184 0.916101 11.9574 0.878313C11.8964 0.840525 11.8258 0.821004 11.7541 0.822062ZM10.9476 2.02114C10.9008 2.21815 10.8753 2.41942 10.8753 2.62352C10.8753 4.06921 12.0546 5.25128 13.5003 5.25128C13.7058 5.25128 13.907 5.22294 14.104 5.17758C13.9062 5.42189 13.6592 5.62189 13.3791 5.76461C13.099 5.90734 12.792 5.98961 12.4781 6.00609C12.1641 6.02257 11.8502 5.9729 11.5567 5.86028C11.2632 5.74766 10.9966 5.57463 10.7743 5.35238C10.552 5.13013 10.3788 4.86364 10.266 4.57018C10.1533 4.27672 10.1035 3.96284 10.1198 3.6489C10.1362 3.33495 10.2183 3.02794 10.3609 2.74777C10.5035 2.4676 10.7034 2.22052 10.9476 2.02256V2.02114ZM4.87567 20.9995H19.1271C19.1768 20.9985 19.2262 21.0076 19.2723 21.0262C19.3184 21.0448 19.3603 21.0726 19.3954 21.1078C19.4305 21.143 19.4581 21.185 19.4765 21.2312C19.495 21.2773 19.5039 21.3268 19.5027 21.3765V22.5004H4.50007V21.3765C4.50007 21.1625 4.65882 20.9995 4.87425 20.9995H4.87567Z"
					fill="#3B4057"
				/>
			</svg>
		),
		isBottomNav: true,
	},
	faq: {
		route: '/faq',
		title: 'FAQ',
		element: <ContactUsPage />,
		svg: () => (
			<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path
					d="M17 18.43H13L8.54999 21.39C7.88999 21.83 7 21.36 7 20.56V18.43C4 18.43 2 16.43 2 13.43V7.42993C2 4.42993 4 2.42993 7 2.42993H17C20 2.42993 22 4.42993 22 7.42993V13.43C22 16.43 20 18.43 17 18.43Z"
					stroke="#3B4057"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					opacity="0.4"
					d="M12 11.3599V11.1499C12 10.4699 12.42 10.1099 12.84 9.81989C13.25 9.53989 13.66 9.1799 13.66 8.5199C13.66 7.5999 12.92 6.85986 12 6.85986C11.08 6.85986 10.34 7.5999 10.34 8.5199"
					stroke="#3B4057"
					strokeWidth="1.5"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					opacity="0.4"
					d="M11.9955 13.75H12.0045"
					stroke="#3B4057"
					strokeWidth="1.5"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
		),
		isBottomNav: true,
	},
	contactUs: {
		route: '/contact-us',
		title: 'Kontaktieren Sie uns',
		element: <ContactUsPage />,
		svg: WeekScheduleSvg,
		isBottomNav: false,
	},
	success: {
		route: 'de/action/:actionId/success',
		failure: 'de/action/:actionId/failure',
		title: 'Erfolgreich gesendet!',
		element: <SuccessPage />,
	},
	support: {
		route: '/support',
		title: 'Support',
		element: <SupportPage />,
		svg: SettingsSvg,
		isBottomNav: false,
	},
	login: {
		route: '/auth/login',
		title: 'Login',
		element: <UserLoginPage />,
		children: {
			iframe: {
				route: '/iframe/auth/login',
				title: 'Login Iframe',
				element: <UserLoginPage />,
				svg: HomeSvg,
				isBottomNav: false,
			},
		},
	},
	register: {
		route: '/auth/register',
		title: 'Register',
		element: <UserRegisterPage isIframe={false} />,
		children: {
			iframe: {
				route: '/iframe/auth/register',
				title: 'Register Iframe',
				element: <UserRegisterPage isIframe />,
				svg: HomeSvg,
				isBottomNav: false,
			},
		},
	},
	forgetPassword: {
		route: '/auth/forget-password',
		title: 'Forget Password',
		element: <ForgetPasswordPage isIframe={false} />,
		children: {
			iframe: {
				route: '/iframe/auth/forget-password',
				title: 'Forget Password Iframe',
				element: <ForgetPasswordPage isIframe />,
				svg: HomeSvg,
				isBottomNav: false,
			},
		},
	},
	autoLogin: {
		route: '/auth/auto-login',
		title: 'Auto Login',
		element: (
			<AuthGuard>
				<AutoLoginPage />
			</AuthGuard>
		),
	},
	chooseAccount: {
		route: '/auth/choose-account',
		title: 'Choose Account',
		element: (
			<AuthGuard>
				<ChooseAccountPage />
			</AuthGuard>
		),
	},
	logout: {
		route: '/auth/logout',
		title: 'Logout',
		element: <UserLogoutPage />,
	},
	// notFound: {
	// 	route: '*',
	// 	title: '404',
	// 	element: <HomePage />,
	// 	svg: HomeSvg,
	// 	isBottomNav: false,
	// },
	forbidden: {
		route: '/403',
		title: '403',
		element: (
			// <AuthenticationLayout>
			<div className="flex flex-col gap-5 items-center my-16">
				<ErrorSvg />
				<h3>{'Sie haben keine Berechtigung'}</h3>
			</div>
			// </AuthenticationLayout>
		),
	},
};
